import React, { useState, useEffect, useRef } from "react";
import ReactPixel from "react-facebook-pixel";
import qs from "query-string";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import StarIcon from "@material-ui/icons/Star";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import defaultLoginLogo from "../../assets/login-logo-default.png";
import { sendTrackingEventOnce, sendGoogleAdsConversion } from "../../utils/trackingMutex";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    overflowX: "hidden",
    minHeight: "100vh",
    background: "#e5e7eb",
    backgroundImage:
      "radial-gradient(circle at 14% 16%, rgba(148, 163, 184, 0.24) 0%, rgba(148, 163, 184, 0) 42%), radial-gradient(circle at 88% 78%, rgba(148, 163, 184, 0.18) 0%, rgba(148, 163, 184, 0) 42%)",
  },
  rootWithBackground: {
    backgroundColor: "#d1d5db",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" }
  },
  backgroundOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    background:
      "linear-gradient(180deg, rgba(15, 23, 42, 0.36) 0%, rgba(15, 23, 42, 0.2) 100%)",
  },
  backgroundMedia: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 0,
  },
  backgroundImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center center",
    userSelect: "none",
    pointerEvents: "none",
  },
  paper: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 520,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.96)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
    borderRadius: 20,
    boxShadow: "0 26px 60px -35px rgba(15, 23, 42, 0.55)",
    padding: theme.spacing(3.5),
    margin: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 380,
      padding: theme.spacing(2.5),
      margin: theme.spacing(1.5),
    },
  },
  logoImg: {
    display: "block",
    margin: "0 auto 10px",
    maxWidth: 160,
    height: "auto",
  },
  subtitle: {
    textAlign: "center",
    color: "#667085",
    marginBottom: theme.spacing(1),
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  submit: {
    margin: theme.spacing(4, 0, 2),
    fontWeight: "bold",
  },
  planCardsGrid: {
    marginTop: theme.spacing(0.5),
  },
  planCard: {
    borderRadius: 12,
    border: "1px solid #dbe3ee",
    background: "#ffffff",
    padding: theme.spacing(1.2),
    cursor: "pointer",
    transition: "all .2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      transform: "translateY(-1px)",
      boxShadow: "0 10px 20px -18px rgba(15, 23, 42, 0.75)",
    },
  },
  planCardActive: {
    borderColor: theme.palette.primary.main,
    background: "rgba(59, 130, 246, 0.06)",
    boxShadow: "0 10px 20px -18px rgba(15, 23, 42, 0.75)",
  },
  planName: {
    fontWeight: 700,
    color: "#0f172a",
    fontSize: 14,
    marginBottom: 4,
  },
  planMeta: {
    display: "block",
    fontSize: 12,
    color: "#475569",
    lineHeight: 1.45,
  },
  whatsappButton: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    backgroundColor: "#25D366",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 4px 12px #044012",
    cursor: "pointer",
    zIndex: 999,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#1ebe5b",
      transform: "scale(1.1)",
    },
  },
  whatsappIcon: {
    fontSize: 34,
    color: "#fff",
  },
  versionCompany: {
    marginTop: 14,
    textAlign: "center",
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    letterSpacing: "0.02em",
  },
  planCardWrapper: {
    position: "relative",
  },
  recommendedPlan: {
    position: "relative",
  },
  popularBadge: {
    position: "absolute",
    top: -4,
    left: -20,
    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    color: "#ffffff",
    fontSize: 9,
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: "4px",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.4)",
    zIndex: 15,
    whiteSpace: "nowrap",
    transform: "rotate(-45deg)",
    transformOrigin: "center center",
    animation: "$bouncePopular 2s ease-in-out infinite",
  },
  "@keyframes bouncePopular": {
    "0%, 100%": {
      transform: "rotate(-45deg) scale(1)",
    },
    "50%": {
      transform: "rotate(-45deg) scale(1.05)",
    },
  },
  freeTrialBadge: {
    position: "absolute",
    top: -10,
    left: 8,
    right: 8,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 700,
    textAlign: "center",
    padding: "4px 8px",
    borderRadius: "8px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    animation: "pulse 2s ease-in-out infinite",
  },
  noCardBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    marginTop: 8,
    padding: "4px 8px",
    background: "linear-gradient(135deg, #3b82f6 0%, #7d4cec 100%)",
    borderRadius: "6px",
    fontSize: 10,
    fontWeight: 700,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
  },
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
    },
    "50%": {
      transform: "scale(1.02)",
      boxShadow: "0 6px 16px rgba(102, 126, 234, 0.6)",
    },
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Obrigatório"),
  companyName: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Obrigatório"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Obrigatório"),
  phone: Yup.string()
    .required("Obrigatório")
    .test(
      "valid-br-phone",
      "Informe DDD + número (8 ou 9 dígitos).",
      value => {
        const digits = String(value || "").replace(/\D/g, "");
        return digits.length === 10 || digits.length === 11;
      }
    ),
  planId: Yup.string().required("Obrigatório"),
});

const formatSignupPhone = value => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";

  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (rest.length <= 4) {
    return `(${ddd}${rest ? ") " + rest : ""}`;
  }

  if (rest.length <= 8) {
    return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }

  return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
};

const normalizeSignupPhone = value =>
  String(value || "").replace(/\D/g, "").slice(0, 11);

const SignUp = () => {

  const classes = useStyles();
  const history = useHistory();
  const { getPlanList } = usePlans();
  const [plans, setPlans] = useState([]);
  const [branding, setBranding] = useState({
    loginLogo: "/logo.png",
    loginBackground: "",
    loginWhatsapp: "https://wa.me/5500000000000",
    companyName: "Whaticket",
    trialDays: 3,
  });
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);
  const [leadTracked, setLeadTracked] = useState(false);
  const viewContentTrackedRef = useRef(false);
  const plansFetchedRef = useRef(false);
  const setFieldValueRef = useRef(null);
  const [lastSelectedPlan, setLastSelectedPlan] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState({
    loading: false,
    valid: null,
    message: ""
  });

  let companyId = null;
  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  // ConexChat: 06/04/2026 Implementação do Pixel do facebook
  // Inicializando o Facebook Pixel e Google Ads (APENAS UMA VEZ)
  useEffect(() => {
    // Inicializar Facebook Pixel
    ReactPixel.init("514658598141439");
    ReactPixel.pageView();

    // Configurar Google Ads manualmente para evitar duplicação
    if (window.gtag && !window.gtagConfigured) {
      window.gtag('config', 'AW-18067457586', {
        'send_page_view': false  // Desabilita pageview automático
      });
      window.gtagConfigured = true;
    }
  }, []);
  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    planId: "",
  };

  const [user] = useState(initialState);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : process.env.REACT_APP_BACKEND_URL;

  const resolveImageUrl = (value, fallback) => {
    if (!value) return fallback;
    if (value.startsWith("http")) return value;
    if (!backendUrl) return value;
    const normalizedBase = backendUrl.replace(/\/+$/, "");
    const path = value.startsWith("/") ? value : `/${value}`;
    return `${normalizedBase}${path}`;
  };

  const isDefaultBackendBackground = (value) => {
    if (!value) return true;
    const normalized = String(value).toLowerCase();
    return normalized.includes("/public/branding/login-background-default");
  };

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user creation status");
        }

        const data = await response.json();
        const isEnabled = data.userCreation === "enabled";
        setUserCreationEnabled(isEnabled);

        if (!isEnabled) {
          toast.info("Cadastro de novos usuários está desabilitado.");
          history.push("/login");
        }
      } catch (err) {
        console.error("Erro ao verificar userCreation:", err);
        setUserCreationEnabled(false);
        toast.error("Erro ao verificar permissão de cadastro.");
        history.push("/login");
      }
    };

    fetchUserCreationStatus();
  }, [backendUrl, history]);

  useEffect(() => {
    const fetchData = async () => {
      // Evitar múltiplas requisições - executar apenas UMA vez
      if (plansFetchedRef.current) {
        return;
      }

      const planList = await getPlanList({ listPublic: "false" });
      setPlans(planList);

      // Auto-selecionar o primeiro plano
      if (planList.length > 0 && setFieldValueRef.current) {
        const firstPlan = planList[0];
        setFieldValueRef.current("planId", String(firstPlan.id));
        handlePlanSelect(firstPlan);
      }

      // Facebook Pixel: ViewContent quando carrega os planos (apenas uma vez)
      if (planList.length > 0 && !viewContentTrackedRef.current) {
        ReactPixel.track('ViewContent', {
          content_name: 'Planos Disponíveis - Trial',
          content_category: 'subscription_plans',
          content_ids: planList.map(p => String(p.id)),
          content_type: 'product_group',
          num_items: planList.length
        });
        viewContentTrackedRef.current = true;
      }

      plansFetchedRef.current = true;
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 NOVO: Validar número de WhatsApp quando mudar
  // OBS: Removido useEffect antigo que monitorava user.phone (nunca muda)
  // A validação agora é feita diretamente no onChange do TextField

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const [{ data: brandingData }, { data: publicAppName }] =
          await Promise.all([
            openApi.get("/global-config/public-branding"),
            openApi.get("/public-settings/appName", {
              params: { token: "wtV" },
            }),
          ]);

        // Usar trialExpiration que vem no brandingData
        const trialExpirationValue = brandingData.trialExpiration || "3";
        const trialDaysValue = parseInt(String(trialExpirationValue), 10);

        setBranding({
          loginLogo: brandingData.loginLogo || "/logo.png",
          loginBackground: isDefaultBackendBackground(brandingData.loginBackground)
            ? ""
            : brandingData.loginBackground,
          loginWhatsapp: brandingData.loginWhatsapp || "https://wa.me/5500000000000",
          companyName: String(publicAppName || "").trim() || "Whaticket",
          trialDays: trialDaysValue,
        });
      } catch (err) {
        console.error("❌ Erro ao buscar branding:", err);
        // segue com fallback local
      }
    };
    fetchBranding();
  }, []);

  const handleSignUp = async (values) => {
    // Evitar múltiplos submits
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // PASSO 1: Criar usuário
      const response = await openApi.post("/auth/signup", {
        ...values,
        phone: normalizeSignupPhone(values.phone)
      });

      // PASSO 2: Encontrar plano selecionado
      const selectedPlan = plans.find(plan => String(plan.id) === String(values.planId));

      if (selectedPlan) {
        // Salvar valor do plano para o Google Ads na página de sucesso
        localStorage.setItem('trialPlanValue', String(selectedPlan.amount || 0));

        // PASSO 3: Rastreamento com Mutex (não interrompe fluxo se falhar)
        try {
          await sendTrackingEventOnce('signup', async () => {
            // Facebook Pixel: CompleteRegistration
            ReactPixel.track('CompleteRegistration', {
              content_name: 'Cadastro ConexChat - Trial',
              status: 'completed',
              value: selectedPlan.amount || 0,
              currency: 'BRL',
              user_email: values.email
            });

            // Facebook Pixel: StartTrial (Custom)
            ReactPixel.trackCustom('StartTrial', {
              content_name: selectedPlan.name || 'Plano Trial',
              content_ids: [String(selectedPlan.id)],
              content_category: 'trial',
              value: selectedPlan.amount || 0,
              currency: 'BRL',
              predicted_lifetime_value: selectedPlan.amount * 12,
              user_email: values.email,
              user_phone: normalizeSignupPhone(values.phone),
              user_name: values.name,
              company_name: values.companyName,
              trial_duration: 3
            });
          });
        } catch (trackingError) {
          // Não interromper o fluxo se o rastreamento falhar
          console.warn("⚠️ [MUTEX] Erro no rastreamento (não interrompe fluxo):", trackingError);
        }
      }

      // Mostrar toast de sucesso
      toast.success(i18n.t("signup.toasts.success"));

      // PASSO 4: Login automático usando API (não openApi) para atualizar AuthContext
      try {
        // Usar 'api' em vez de 'openApi' para que os interceptores funcionem
        const api = (await import("../../services/api")).default;
        const { data } = await api.post("/auth/login", {
          email: values.email,
          password: values.password
        });

        // Salvar dados do usuário para usar na página de sucesso (fallback)
        localStorage.setItem("signupUserData", JSON.stringify({
          name: values.name,
          email: values.email,
          companyName: values.companyName,
          companyId: data.user.companyId
        }));

        // Redirecionar para a página de sucesso
        setTimeout(() => {
          // Forçar reload para garantir que o AuthContext carregue os dados
          window.location.href = "/signup-success";
        }, 500);

      } catch (loginError) {
        // Mostrar mensagem ao usuário
        toast.error(
          "Cadastro realizado com sucesso! Por favor, faça login com suas credenciais."
        );

        // Redirecionar para login após um delay
        setTimeout(() => {
          history.push("/login");
          setIsSubmitting(false);
        }, 2000);
      }

    } catch (err) {
      console.error("❌ Erro no cadastro:", err);
      toastError(err);
      setIsSubmitting(false);
    }
  };

  // Facebook Pixel: Lead quando usuário começa a preencher o formulário
  const handleFieldBlur = (fieldName, value) => {
    if (fieldName === 'companyName' && value && value.length > 2) {
      // Usar Mutex para garantir execução ÚNICA do evento Lead
      sendTrackingEventOnce('lead', async () => {
        try {
          // Facebook Pixel: Lead
          ReactPixel.track('Lead', {
            content_name: 'Potencial Lead - ConexChat Trial',
            content_category: 'signup_form',
            value: 'trial_signup'
          });
        } catch (fbError) {
          console.warn("⚠️ [MUTEX] Erro ao enviar Facebook Pixel:", fbError);
        }

        // Google Ads: Lead Qualificado
        await sendGoogleAdsConversion('lead', 'JF5yCLuMm5ccELKMnqdD', 0, 'BRL');

        setLeadTracked(true);
      });
    }
  };

  // Facebook Pixel: SelectPlan (Custom) quando usuário seleciona um plano
  const handlePlanSelect = (plan) => {
    // Evita disparar o evento múltiplas vezes para o mesmo plano
    if (lastSelectedPlan !== plan.id) {
      // CORREÇÃO: Usar trackCustom para eventos customizados
      ReactPixel.trackCustom('SelectPlan', {
        content_name: plan.name,
        content_ids: [String(plan.id)],
        content_category: 'plan_selection',
        value: plan.amount,
        currency: 'BRL',
        users: plan.users,
        connections: plan.connections,
        queues: plan.queues
      });
      setLastSelectedPlan(plan.id);
    }
  };

  // 🔹 NOVO: Validação de WhatsApp com debounce
  const validatePhone = async (phoneNumber) => {
    const cleanNumber = phoneNumber.replace(/\D/g, "");

    // Não validar se estiver vazio ou incompleto
    if (!cleanNumber || cleanNumber.length < 10) {
      setPhoneValidation({
        loading: false,
        valid: null,
        message: ""
      });
      return;
    }

    setPhoneValidation({ loading: true, valid: null, message: "Validando número no WhatsApp..." });

    try {
      // Adicionar código do país 55 (Brasil) antes de enviar para validação
      const numberWithCountryCode = `55${cleanNumber}`;

      const response = await openApi.post("/public/whatsapp/check", {
        number: numberWithCountryCode
      });

      if (response.data.valid) {
        setPhoneValidation({
          loading: false,
          valid: true,
          message: "Número válido no WhatsApp ✓"
        });
      } else {
        setPhoneValidation({
          loading: false,
          valid: false,
          message: "Número não encontrado no WhatsApp ✗"
        });
      }
    } catch (err) {
      setPhoneValidation({
        loading: false,
        valid: false,
        message: "Erro ao validar número"
      });
    }
  };

  // Debounce para validar apenas após 1 segundo sem digitação
  let debounceTimer;
  const debouncedValidatePhone = (phone) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      validatePhone(phone);
    }, 1000);
  };

  if (!userCreationEnabled) {
    return null;
  }

  return (
    <div className={`${classes.root} ${branding.loginBackground ? classes.rootWithBackground : ""}`}>
      {!!branding.loginBackground && (
        <>
          <div className={classes.backgroundMedia}>
            <img
              src={resolveImageUrl(branding.loginBackground, "")}
              alt="Background cadastro"
              className={classes.backgroundImg}
            />
          </div>
          <div className={classes.backgroundOverlay} />
        </>
      )}
      <div className={classes.paper}>
        <img
          src={resolveImageUrl(branding.loginLogo, defaultLoginLogo)}
          alt="Logo"
          className={classes.logoImg}
        />
        <Typography component="h1" variant="h5" style={{ marginBottom: 8 }}>
          {i18n.t("signup.title")}
        </Typography>
        <Typography className={classes.subtitle}>
          Crie sua conta e escolha o melhor plano.
        </Typography>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={async (values, actions) => {
            await handleSignUp(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, values, setFieldValue }) => {
            // Armazenar setFieldValue na ref para uso posterior
            if (setFieldValueRef.current !== setFieldValue) {
              setFieldValueRef.current = setFieldValue;
            }

            return (
              <Form className={classes.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="companyName"
                      label={i18n.t("signup.form.company")}
                      error={touched.companyName && Boolean(errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                      name="companyName"
                      autoComplete="companyName"
                      autoFocus
                      onBlur={(e) => handleFieldBlur('companyName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      autoComplete="name"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      fullWidth
                      id="name"
                      label={i18n.t("signup.form.name")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="email"
                      label={i18n.t("signup.form.email")}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      autoComplete="email"
                      inputProps={{ style: { textTransform: "lowercase" } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      name="password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      label={i18n.t("signup.form.password")}
                      type="password"
                      id="password"
                      autoComplete="current-password"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      id="phone"
                      label={i18n.t("signup.form.phone")}
                      name="phone"
                      autoComplete="tel-national"
                      placeholder="(00) 00000-0000"
                      value={formatSignupPhone(values.phone)}
                      onChange={(e) => {
                        const formattedPhone = formatSignupPhone(e.target.value);
                        setFieldValue("phone", formattedPhone);
                        debouncedValidatePhone(formattedPhone);
                      }}
                      error={
                        (touched.phone && Boolean(errors.phone)) ||
                        (phoneValidation.valid === false)
                      }
                      helperText={
                        phoneValidation.message ||
                        (touched.phone && errors.phone)
                      }
                      FormHelperTextProps={{
                        style: {
                          color:
                            phoneValidation.valid === true
                              ? "#10b981"
                              : phoneValidation.valid === false
                              ? "#ef4444"
                              : "inherit",
                          fontWeight: phoneValidation.valid !== null ? 600 : "normal"
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <>
                            {phoneValidation.loading && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "#6b7280",
                                  animation: "spin 1s linear infinite"
                                }}
                              >
                                ⏳
                              </span>
                            )}
                            {!phoneValidation.loading && phoneValidation.valid === true && (
                              <CheckCircleIcon
                                style={{ color: "#10b981", fontSize: 20 }}
                              />
                            )}
                            {!phoneValidation.loading && phoneValidation.valid === false && (
                              <CancelIcon
                                style={{ color: "#ef4444", fontSize: 20 }}
                              />
                            )}
                          </>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 12,
                      flexWrap: 'wrap',
                      gap: 8
                    }}>
                      <Typography variant="subtitle1" style={{
                        fontWeight: 600,
                        color: '#0f172a',
                        textAlign: 'center'
                      }}>
                        Escolha Seu Plano
                      </Typography>
                      <Typography variant="caption" style={{
                        background: '#ef4444',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: 10
                      }}>
                        Obrigatório
                      </Typography>
                    </div>
                    <Field type="hidden" name="planId" />
                    <Grid container spacing={2} className={classes.planCardsGrid}>
                      {plans.map((plan, index) => (
                        <Grid item xs={12} sm={6} key={plan.id}>
                          <div className={classes.planCardWrapper}>
                            <div
                              className={`${classes.planCard} ${String(values.planId) === String(plan.id)
                                ? classes.planCardActive
                                : ""
                                }`}
                              onClick={() => {
                                handlePlanSelect(plan);
                                setFieldValue("planId", String(plan.id));
                              }}
                              role="button"
                              tabIndex={0}
                              style={{
                                paddingTop: 16,
                                position: 'relative',
                                ...(index === 0 ? {
                                  border: '2px solid #f59e0b',
                                  background: '#ffffff',
                                  boxShadow: '0 4px 16px rgba(245, 158, 11, 0.2)'
                                } : {}),
                                ...(index === 1 ? {
                                  border: '2px solid #8b5cf6',
                                  background: '#ffffff'
                                } : {}),
                                ...(index === 2 ? {
                                  border: '2px solid #22c55e',
                                  background: '#ffffff'
                                } : {}),
                                ...(String(values.planId) === String(plan.id) ? {
                                  boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.1)'
                                } : {})
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  handlePlanSelect(plan);
                                  setFieldValue("planId", String(plan.id));
                                }
                              }}
                            >
                              {String(values.planId) === String(plan.id) && (
                                <div style={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  background: '#10b981',
                                  borderRadius: '50%',
                                  width: 28,
                                  height: 28,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
                                  zIndex: 20,
                                  animation: 'pulse 2s ease-in-out infinite'
                                }}>
                                  <CheckCircleIcon style={{ fontSize: 20, color: '#fff' }} />
                                </div>
                              )}
                              <div className={classes.freeTrialBadge} style={{
                                ...(index === 0 ? {
                                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.4)",
                                } : {}),
                                ...(index === 1 ? {
                                  background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                  boxShadow: "0 4px 12px rgba(139, 92, 246, 0.4)",
                                } : {}),
                                ...(index === 2 ? {
                                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                                  boxShadow: "0 4px 12px rgba(34, 197, 94, 0.4)",
                                } : {})
                              }}>
                                <StarIcon style={{ fontSize: 14 }} />
                                <span>Grátis {branding.trialDays} Dias</span>
                              </div>
                              <Typography className={classes.planName} style={{ marginTop: 8 }}>
                                {plan.name}
                              </Typography>
                              <span className={classes.planMeta}>
                                Atendentes: {plan.users}
                              </span>
                              <span className={classes.planMeta}>
                                WhatsApp: {plan.connections}
                              </span>
                              <span className={classes.planMeta}>
                                Filas: {plan.queues}
                              </span>
                              <span className={classes.planMeta}>
                                Valor: R$ {plan.amount}
                              </span>
                              <div className={classes.noCardBadge}>
                                <CancelOutlinedIcon style={{ fontSize: 12 }} />
                                <span>Não precisa de cartão</span>
                              </div>
                            </div>
                          </div>
                        </Grid>
                      ))}
                    </Grid>
                    {touched.planId && errors.planId && (
                      <div style={{
                        background: '#fee2e2',
                        border: '1px solid #ef4444',
                        borderRadius: 8,
                        padding: '10px 12px',
                        marginTop: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <span style={{ fontSize: 18, color: '#ef4444' }}>⚠️</span>
                        <Typography
                          variant="caption"
                          style={{ color: "#b91c1c", fontWeight: 600, flex: 1 }}
                        >
                          Por favor, selecione um plano para continuar
                        </Typography>
                      </div>
                    )}
                  </Grid>
                </Grid>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={
                    isSubmitting ||
                    phoneValidation.loading ||
                    phoneValidation.valid === false
                  }
                >
                  {isSubmitting ? (
                    <>
                      <span style={{ marginRight: 8 }}>Processando...</span>
                      <span className="loader" style={{
                        display: 'inline-block',
                        width: 16,
                        height: 16,
                        border: '2px solid #fff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                    </>
                  ) : (
                    i18n.t("signup.buttons.submit")
                  )}
                </Button>
                <Grid container justifyContent="center">
                  <Grid item>
                    <Link
                      href="#"
                      variant="body2"
                      component={RouterLink}
                      to="/login"
                    >
                      {i18n.t("signup.buttons.login")}
                    </Link>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
        <Typography className={classes.versionCompany}>
          {`v15.0.2 ${branding.companyName}`}
        </Typography>
      </div>
      <div
        className={classes.whatsappButton}
        onClick={() => window.open(branding.loginWhatsapp)}
      >
        <WhatsAppIcon className={classes.whatsappIcon} />
      </div>
    </div>
  );
};

export default SignUp;
